import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import LanguageSwitcher from 'LanguageSwitcher';

const Navigation = ({ language, setLanguage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const translations = {
    en: {
      home: 'Home',
      about: 'About',
      classes: 'Classes',
      trainers: 'Trainers',
      membership: 'Membership',
      contact: 'Contact',
      adminLogin: 'Admin Login'
    },
    ar: {
      home: 'الرئيسية',
      about: 'من نحن',
      classes: 'الفصول',
      trainers: 'المدربين',
      membership: 'العضوية',
      contact: 'اتصل بنا',
      adminLogin: 'دخول المدير'
    }
  };

  const t = translations[language];
  const isRTL = language === 'ar';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
    } ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <div className="flex items-center">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-green-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3`}>
              <Icon name="Dumbbell" size={20} className="sm:w-6 sm:h-6" color="white" />
            </div>
            <span className={`text-lg sm:text-xl font-bold ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}>
              FitPro
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('hero')}
              className={`text-sm font-medium transition-colors ${
                isScrolled 
                  ? 'text-gray-700 hover:text-green-600' :'text-white hover:text-green-400'
              }`}
            >
              {t.home}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`text-sm font-medium transition-colors ${
                isScrolled 
                  ? 'text-gray-700 hover:text-green-600' :'text-white hover:text-green-400'
              }`}
            >
              {t.about}
            </button>
            <button
              onClick={() => scrollToSection('classes')}
              className={`text-sm font-medium transition-colors ${
                isScrolled 
                  ? 'text-gray-700 hover:text-green-600' :'text-white hover:text-green-400'
              }`}
            >
              {t.classes}
            </button>
            <button
              onClick={() => scrollToSection('trainers')}
              className={`text-sm font-medium transition-colors ${
                isScrolled 
                  ? 'text-gray-700 hover:text-green-600' :'text-white hover:text-green-400'
              }`}
            >
              {t.trainers}
            </button>
            <button
              onClick={() => scrollToSection('membership')}
              className={`text-sm font-medium transition-colors ${
                isScrolled 
                  ? 'text-gray-700 hover:text-green-600' :'text-white hover:text-green-400'
              }`}
            >
              {t.membership}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-sm font-medium transition-colors ${
                isScrolled 
                  ? 'text-gray-700 hover:text-green-600' :'text-white hover:text-green-400'
              }`}
            >
              {t.contact}
            </button>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Language Toggle */}
         <LanguageSwitcher />

            {/* Admin Login - Desktop */}
            <Link
              to="/admin-login"
              className="hidden sm:inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs sm:text-sm font-medium"
            >
              <Icon name="Shield" size={16} className="mr-1 sm:mr-2" />
              {t.adminLogin}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isScrolled 
                  ? 'text-gray-700 hover:bg-gray-100' :'text-white hover:bg-white/10'
              }`}
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-2 space-y-1">
            <button
              onClick={() => scrollToSection('hero')}
              className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t.home}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t.about}
            </button>
            <button
              onClick={() => scrollToSection('classes')}
              className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t.classes}
            </button>
            <button
              onClick={() => scrollToSection('trainers')}
              className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t.trainers}
            </button>
            <button
              onClick={() => scrollToSection('membership')}
              className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t.membership}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t.contact}
            </button>
            <Link
              to="/admin-login"
              className="flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Icon name="Shield" size={16} className="mr-2" />
              {t.adminLogin}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;